import { AlfaIcon, GazprombankIcon, OzonbankIcon } from '@p2p/assets/svg';
import { TGetOrderResponse } from '@p2p/services';
import classNames from 'classnames';
import { MouseEvent, ReactNode } from 'react';

import { useDeepLink } from '../DeepLink/useDeepLink';
import { QuickPayBank, QuickPayBankCode, QuickPayLink } from './quickPay.banks';
import styles from './QuickPay.module.scss';
import { getAvailableQuickPayBank, getQuickPayPlatform } from './quickPay.utils';
import { useQuickPay } from './useQuickPay';

type QuickPayProps = {
  order: TGetOrderResponse;
};

/**
 * P2P-3036/P2P-3037: блок быстрой оплаты тем gazprom_page и ozon_page — подпись и кнопка
 * «Оплатить в один клик», сразу под реквизитами. Условия не выполнены (другая тема или
 * client_bank_code, реквизит не подходит, нет ссылки для этой площадки, после неудачной
 * попытки) - блок не рендерится, остальное смещается вверх.
 *
 * P2P-3088: у Озона блок есть и на main_page, и на десктопе (там кнопка ведёт в веб-версию
 * банка в новой вкладке).
 *
 * P2P-3112: Альфа-Банк - на main_page для перевода на карту Таджикистана, тоже с десктопа.
 */
export const QuickPay = ({ order }: QuickPayProps) => {
  const { isFail } = useDeepLink({ order });

  const bank = getAvailableQuickPayBank({
    order,
    isFail,
    userAgent: window.navigator.userAgent,
    maxTouchPoints: window.navigator.maxTouchPoints,
  });
  const link = bank?.resolveLink(order);

  return bank && link ? <QuickPayBlock order={order} bank={bank} link={link} /> : null;
};

const LOGOS: Record<QuickPayBankCode, ReactNode> = {
  // P2P-3036: «глобус» из вложения задачи и надпись, как на макете.
  gazprom: (
    <span className={styles.logo_gazprom}>
      <GazprombankIcon className={styles.logo_icon} aria-hidden />
      ГАЗПРОМБАНК
    </span>
  ),
  // P2P-3037: логотип Ozon Банка из вложения задачи («ozon» и «банк» в две строки).
  ozon: <OzonbankIcon className={styles.logo_ozon} role="img" aria-label="Ozon Банк" />,
  // P2P-3112: логотипа во вложениях задачи нет - значок Альфы с кнопки «Быстрая оплата» и надпись.
  alfa: (
    <span className={styles.logo_alfa}>
      <AlfaIcon className={styles.logo_icon} aria-hidden />
      Альфа-Банк
    </span>
  ),
};

type QuickPayBlockProps = QuickPayProps & { bank: QuickPayBank; link: QuickPayLink };

const QuickPayBlock = ({ order, bank, link }: QuickPayBlockProps) => {
  const { launch } = useQuickPay(order, bank, link);
  // P2P-3088: на десктопе кнопка ведёт в веб-версию банка и открывается в новой вкладке -
  // страница с реквизитами и таймером должна остаться на месте.
  const isDesktop = getQuickPayPlatform() === 'desktop';

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    launch();
  };

  return (
    <div className={styles.container}>
      <p className={styles.caption}>{bank.caption}</p>
      <a
        className={classNames(styles.button, styles[`button_${bank.code}`])}
        href={isDesktop ? link.webUrl : link.appLink}
        target={isDesktop ? '_blank' : undefined}
        rel={isDesktop ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
      >
        {LOGOS[bank.code]}
        <span className={styles.divider} aria-hidden />
        <span className={styles.action}>
          Оплатить в один клик
          <span className={styles.arrow} aria-hidden>
            →
          </span>
        </span>
      </a>
    </div>
  );
};
